import { Injectable } from '@angular/core';
import { User } from '@interfaces/interface-bakend/seguridad/users/user.interface';
import { SignoGecrosAfiliado } from '@interfaces/interface-bakend/entidades/signo-greco-afiliado.interface';
import { buildComparablePayload, fingerprint } from '@shared/utils/gecros.utils';
import { GecrosAfiliadosService } from './gecros-afiliados.service';
import { SignoPlanService } from './signo-planes.service';
import { UsuarioService } from '../shared/usuario.service';

export interface FamiliarGecrosData {
    familiarId: string;
    documento: string;
}

/**
 * Mantiene nuestra DB sincronizada con GECROS.
 *
 * GECROS es la fuente de verdad de la cobertura y el plan, así que siempre se
 * consulta fresco (sin cache) y se patchea el usuario solo cuando algo cambió
 * realmente. Incluye el ObjectId del plan local resuelto.
 */
@Injectable({ providedIn: 'root' })
export class GecrosSyncService {

    constructor(
        private gecrosAfiliadosService: GecrosAfiliadosService,
        private signoPlanService: SignoPlanService,
        private usuarioService: UsuarioService,
    ) { }

    /**
     * Sincroniza un usuario. Devuelve true cuando actualizó nuestra DB.
     */
    async sincronizarUsuario(
        userId: string,
        identificacion: string,
        usuarioActual?: User
    ): Promise<boolean> {
        if (!userId || !identificacion) {
            return false;
        }

        try {
            const gecrosDisponible = await this.gecrosAfiliadosService.statusGecros();
            if (!gecrosDisponible) {
                return false;
            }

            const resp = await this.gecrosAfiliadosService.getAfiliadoByIdentificacion(identificacion);
            const afiliado: SignoGecrosAfiliado = resp?.data?.[0];
            if (!afiliado) {
                return false;
            }

            return await this.sincronizarAfiliado(userId, afiliado, usuarioActual);
        } catch (error) {
            console.error(`[GecrosSync] Error sincronizando documento ${identificacion}:`, error);
            return false;
        }
    }

    /**
     * Patchea un usuario con un payload de GECROS ya obtenido. Evita una
     * segunda llamada a GECROS cuando quien invoca (ej. la credencial) ya tiene
     * el dato.
     */
    async sincronizarAfiliado(
        userId: string,
        afiliado: SignoGecrosAfiliado,
        usuarioActual?: User
    ): Promise<boolean> {
        if (!userId || !afiliado) {
            return false;
        }

        try {
            const planLocal = await this.signoPlanService.resolverPlanGecros(afiliado.plan);
            const planId = planLocal?._id ?? null;

            if (afiliado.plan && !planId) {
                console.warn(
                    `[GecrosSync] No se pudo mapear el plan GECROS "${afiliado.plan}" ` +
                    `para el usuario ${userId}`
                );
            }

            const usuario = usuarioActual ?? await this.usuarioService.getFindOne(userId);
            if (!usuario) {
                return false;
            }

            const afiliadoCambio =
                fingerprint(buildComparablePayload(afiliado)) !==
                fingerprint(buildComparablePayload(usuario.signoGecrosAfiliado));

            // Nunca borrar un plan existente porque GECROS no se pudo mapear.
            const planCambio =
                !!planId && String(usuario.signoPlan ?? '') !== String(planId);

            if (!afiliadoCambio && !planCambio) {
                return false;
            }

            await this.usuarioService.updateUser(userId, {
                signoGecrosAfiliado: afiliado,
                signoPlan: planCambio ? planId : usuario.signoPlan,
            } as User);

            return true;
        } catch (error) {
            console.error(`[GecrosSync] Error aplicando datos GECROS al usuario ${userId}:`, error);
            return false;
        }
    }

    /**
     * Sincroniza el titular y, opcionalmente, a cada familiar. Seguro para
     * fire-and-forget: cada usuario se maneja por separado y nunca lanza.
     */
    async sincronizarTitularYFamiliares(
        userId: string,
        identificacion: string,
        familiares?: FamiliarGecrosData[]
    ): Promise<void> {
        if (identificacion) {
            await this.sincronizarUsuario(userId, identificacion);
        }

        if (familiares?.length) {
            await Promise.all(
                familiares
                    .filter(familiar => familiar?.familiarId && familiar?.documento)
                    .map(familiar =>
                        this.sincronizarUsuario(familiar.familiarId, familiar.documento)
                            .catch(error => console.error('[GecrosSync] Error en familiar:', error))
                    )
            );
        }
    }
}
